import { useState, useCallback, useEffect } from "react";
import qs from "qs";
import { toast } from "react-toastify";
import { useRouter } from "next/router";
import { BsFillFileTextFill, BsClock, BsClockHistory } from "react-icons/bs";
import { FaChevronRight } from "react-icons/fa";
import StatsPanel from "~/shared/StatsPanel";
import LinkButton from "~/shared/LinkButton";
import useAuth from "~/hooks/useAuth";
import { AnyRecord } from "~/types/record";
import Title from "~/shared/Title";
import Jobs from "~/services/jobs";
import Spinner from "~/shared/Spinner";
import { formatDate, sumDays } from "~/utils/date";
import JobSmall from "~/shared/JobSmall";
import JobSmallWithThumbs from "~/shared/JobSmallWithThumb";
import { LocatedJob } from "~/types/jobs";
import FlatButton from "~/shared/FlatBtn";
import TextInput from "~/shared/TextInput";
import Btn from "~/shared/Btn";
import Panel from "~/shared/Panel";

const DashboardPage = () => {
    const router = useRouter();
    const { user, account } = useAuth();

    const [fetchingStats, setFetchingStats] = useState(false);
    const [fetchingNextJobs, setFetchingNextJobs] = useState(false);
    const [fetchingDeliveredJobs, setFetchingDeliveredJobs] = useState(false);

    const [stats, setStats] = useState<AnyRecord>({});
    const [nextJobs, setNextJobs] = useState<LocatedJob[]>([]);
    const [deliveredJobs, setDeliveredJobs] = useState<LocatedJob[]>([]);

    const [searchTerm, setSearchTerm] = useState("");

    const goTo = (filter: AnyRecord) => {
        const query = qs.stringify(filter);
        router.push(`/jobs/list?${query}`);
    };

    const getStats = useCallback(() => {
        setFetchingStats(true);
        Jobs.stats()
            .then((response) => {
                setStats(response.stats);
            })
            .finally(() => setFetchingStats(false));
    }, []);

    const getNextJobs = useCallback(async () => {
        setFetchingNextJobs(true);

        const startDate = `${formatDate(new Date(), "yyyy-MM-dd")} 00:00:00`;
        const endDate = `${formatDate(sumDays(new Date(), 30), "yyyy-MM-dd")} 23:59:59`;

        const date = [startDate, endDate];

        Jobs.search(1, { date, statuses: ["aberto", "agendamento_confirmado"] }, { limit: 4 })
            .then((res) => {
                setNextJobs(res.data.jobs);
            })
            .catch((e) => {
                return toast.error(`Não foi possível recuperar os jobs: ${e?.response?.data?.msg ?? e.message}`);
            })
            .finally(() => setFetchingNextJobs(false));
    }, []);

    const getDeliveredJobs = useCallback(async () => {
        setFetchingDeliveredJobs(true);

        Jobs.search(1, { statuses: ["material_disponibilizado", "finalizado_cliente"] }, { limit: 4, delivered: true })
            .then((res) => {
                setDeliveredJobs(res.data.jobs);
            })
            .catch((e) => {
                return toast.error(`Não foi possível recuperar os jobs: ${e?.response?.data?.msg ?? e.message}`);
            })
            .finally(() => setFetchingDeliveredJobs(false));
    }, []);

    useEffect(() => {
        getStats();
        getNextJobs();
        getDeliveredJobs();
    }, [getStats, getNextJobs, getDeliveredJobs]);

    return (
        <div className="container pb-5">
            <Title level="1">Olá, {user?.nome}. Seja bem-vindo ao Fotop Serviços</Title>

            {
                !user?.canBook ?
                    <Panel className="mt-4">
                        <Title level="3">
                            A Fotop uniu sua área de serviços imobiliários à Piktiz,
                            que agora será responsável pelo atendimento a todos os clientes de serviços recorrentes
                            para empresas B2B.
                            Com uma vasta experiência nesse setor, a Piktiz assumirá a gestão de todos os
                            agendamentos a partir de agora.
                        </Title>
                        <p className="mt-4 ">
                            Clique
                            <a target="_blank"
                               href="https://wa.me/5554999965119?text=Ol%C3%A1!%20Vim%20pela%20Fotop%20e%20gostaria%20de%20agendar%20minha%20sess%C3%A3o%20de%20fotos.%20"
                               style={{textDecoration: 'none'}}> aqui </a>
                            para agendar sua sessão com a Piktiz.
                        </p>
                    </Panel>
                    : ""
            }

            <div className="row my-4">
                <div className="col-12 mb-3">
                    <Title featured level="2" icon={<BsFillFileTextFill />}>
                        Jobs
                    </Title>
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                    <LinkButton block onClick={() => goTo({ statuses: ["aguardando_agendamento"] })}>
                        <StatsPanel
                            loading={fetchingStats}
                            value={(stats?.aguardando_agendamento ?? 0).toString().padStart(2, "0")}
                            label={() => (
                                <>
                                    Aguardando
                                    <br />
                                    agendamento
                                </>
                            )}
                        />
                    </LinkButton>
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                    <LinkButton block onClick={() => goTo({ statuses: ["aberto"] })}>
                        <StatsPanel
                            loading={fetchingStats}
                            value={(stats?.aberto ?? 0).toString().padStart(2, "0")}
                            label={() => (
                                <>
                                    Agendamentos
                                    <br />
                                    abertos
                                </>
                            )}
                        />
                    </LinkButton>
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                    <LinkButton block onClick={() => goTo({ statuses: ["agendamento_confirmado"] })}>
                        <StatsPanel
                            loading={fetchingStats}
                            value={(stats?.agendamento_confirmado ?? 0).toString().padStart(2, "0")}
                            label={() => (
                                <>
                                    Agendamentos
                                    <br />
                                    confirmados
                                </>
                            )}
                        />
                    </LinkButton>
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                    <LinkButton block onClick={() => goTo({ statuses: ["agendamento_recusado"] })}>
                        <StatsPanel
                            loading={fetchingStats}
                            value={(stats?.agendamento_recusado ?? 0).toString().padStart(2, "0")}
                            label={() => (
                                <>
                                    Agendamentos
                                    <br />
                                    recusados
                                </>
                            )}
                        />
                    </LinkButton>
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                    <LinkButton block onClick={() => goTo({ statuses: ["em_andamento"] })}>
                        <StatsPanel
                            loading={fetchingStats}
                            value={((stats?.em_andamento ?? 0) + (stats?.material_recebido ?? 0))
                                .toString()
                                .padStart(2, "0")}
                            label={() => (
                                <>
                                    Em
                                    <br />
                                    andamento
                                </>
                            )}
                        />
                    </LinkButton>
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                    <LinkButton block onClick={() => goTo({ statuses: ["material_disponibilizado"] })}>
                        <StatsPanel
                            loading={fetchingStats}
                            value={(stats?.material_disponibilizado ?? 0).toString().padStart(2, "0")}
                            label={() => (
                                <>
                                    Jobs não
                                    <br />
                                    baixados
                                </>
                            )}
                        />
                    </LinkButton>
                </div>
            </div>

            <form action="/jobs/list" method="get">
                <div className="row my-4">
                    <div className="col-12 mb-3">
                        <Title featured level="2">
                            Buscar jobs
                        </Title>
                    </div>

                    <div className="col-12 col-md-10">
                        <TextInput name="search" value={searchTerm} onChange={(_, value) => setSearchTerm(value)} placeholder="Buscar" />
                    </div>
                    <div className="col-12 col-md-2">
                        <Btn type="submit" block>
                            Consultar jobs
                        </Btn>
                    </div>
                </div>
            </form>

            {account?.grantAllJobsAccess ? (
                <div className="row my-4">
                    <div className="col-12 mb-3">
                        <Title
                            featured
                            level="2"
                            icon={<BsClock />}
                            actions={
                                <FlatButton
                                    onClick={() => router.push("/gallery/list")}
                                    icon={<FaChevronRight />}
                                >
                                    Ver todos
                                </FlatButton>
                            }
                        >
                            Últimos jobs entregues
                        </Title>
                    </div>

                    <div className="col-12">
                        {fetchingDeliveredJobs ? (
                            <Spinner size="medium" />
                        ) : (
                            <div className="row">
                                {!deliveredJobs.length ? (
                                    <div className="col-12">
                                        <p>Nenhum job encontrado</p>
                                    </div>
                                ) : (
                                    ""
                                )}

                                {deliveredJobs.map((job) => (
                                    <div className="col-12 col-md-3" key={job.id}>
                                        <JobSmallWithThumbs job={job} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                ""
            )}

            <div className="row my-4">
                <div className="col-12 mb-3">
                    <Title
                        featured
                        level="2"
                        icon={<BsClockHistory />}
                        actions={
                            <FlatButton
                                icon={<FaChevronRight />}
                                onClick={() => router.push("/jobs/list")}
                            >
                                Ver todos
                            </FlatButton>
                        }
                    >
                        Próximos jobs
                    </Title>
                </div>

                <div className="col-12">
                    {fetchingNextJobs ? (
                        <Spinner size="medium" />
                    ) : (
                        <div className="row">
                            {!nextJobs.length ? (
                                <div className="col-12">
                                    <p>Nenhum job encontrado</p>
                                </div>
                            ) : (
                                ""
                            )}

                            {nextJobs.map((job) => (
                                <div className="col-12 col-md-4" key={job.id}>
                                    <JobSmall job={job} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
